/* FILE: ./frontend/src/features/canada/suppliers/SupplierDirectory.jsx */
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SupplierCard from './SupplierCard';
import SearchFilters from '../components/SearchFilters';
import ArbitrageMatrix from '../../../components/ArbitrageMatrix'; 
import { useAuth } from '../../../contexts/AuthContext';

const SupplierDirectory = ({ api }) => {
  const { user, userProfile, isSupplier } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation(); 
  
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [error, setError] = useState('');
  const [searchLoading, setSearchLoading] = useState(false);

  // Arbitrage Matrix State
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [showMatrix, setShowMatrix] = useState(false);

  // Keep track of active search state
  const [activeSearchTerm, setActiveSearchTerm] = useState('');
  const [activeProvince, setActiveProvince] = useState('ON');
  const [activeCategory, setActiveCategory] = useState('');
  const [activeCapabilities, setActiveCapabilities] = useState('');
  const [activeShowMyListings, setActiveShowMyListings] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const urlSearch = params.get('search') || '';
    const urlProvince = params.get('province') || ''; 
    const urlCategory = params.get('category') || '';
    const urlCapabilities = params.get('capabilities') || '';
    
    setActiveSearchTerm(urlSearch);
    if (urlProvince) setActiveProvince(urlProvince);
    setActiveCategory(urlCategory);
    setActiveCapabilities(urlCapabilities);

    fetchSuppliersWithParams(urlSearch, urlProvince || 'ON', urlCategory, urlCapabilities, false);
  }, [user, location.search]); // eslint-disable-line

  const fetchSuppliersWithParams = async (searchVal, provVal, catVal, capsVal, mySuppliersVal) => {
    try {
      setError('');
      setSearchLoading(true);
      
      const params = new URLSearchParams();
      
      if (searchVal) params.append('search', searchVal);
      // ⚡ FIXED: Removed province append here so it stops filtering out out-of-province suppliers
      if (catVal) params.append('category', catVal);
      if (capsVal) params.append('capabilities', capsVal);

      if (mySuppliersVal && user) {
        params.append('user_id', user.id);
      }

      if (user) {
        params.append('caller_id', user.id);
      }

      const response = await api.get(`/suppliers?${params}`);
      setSuppliers(response.data.data || response.data || []);

    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setError('Failed to load suppliers.');
      setSuppliers([]);
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  const handleSearchSubmit = (filters) => {
    setActiveSearchTerm(filters.search);
    setActiveProvince(filters.province);
    setActiveCategory(filters.category);
    setActiveCapabilities(filters.capabilities);
    setActiveShowMyListings(filters.showMyListings);
    
    setSelectedSuppliers([]);
    setShowMatrix(false);
    
    fetchSuppliersWithParams(
        filters.search, 
        filters.province, 
        filters.category, 
        filters.capabilities, 
        filters.showMyListings
    );

    // Update URL to reflect the current search for shareability
    const newParams = new URLSearchParams();
    if (filters.search) newParams.append('search', filters.search);
    if (filters.province) newParams.append('province', filters.province);
    if (filters.category) newParams.append('category', filters.category);
    if (filters.capabilities) newParams.append('capabilities', filters.capabilities);
    
    navigate({ search: newParams.toString() }, { replace: true });
  };

  const handleToggleSelect = (supplier) => {
    setSelectedSuppliers(prev => {
      const isSelected = prev.some(s => s.id === supplier.id);
      if (isSelected) {
        return prev.filter(s => s.id !== supplier.id);
      } else {
        if (prev.length >= 10) {
          alert('Arbitrage limits: You can only compare up to 10 suppliers at a time.');
          return prev;
        }
        return [...prev, supplier];
      }
    });
  };

  // ⚡ OPTION A FOR RFQ: Route directly to /rfq/new with preselectedSupplier and active search context
  const handleDirectRfqRoute = (supplier) => {
    navigate('/rfq/new', {
      state: {
        preselectedSupplier: supplier,
        autoFillCode: activeSearchTerm,
        autoFillProvince: activeProvince || 'ON'
      }
    });
  };

  // ⚡ ARBITRAGE MATRIX GATEKEEPER ($99 Standard & $199 High Volume Pro Buyers)
  const handleLaunchMatrix = () => {
    if (!user) {
      navigate('/login');
      return;
    }
    const isProOrAdmin = userProfile?.buyer_tier === 'pro' || userProfile?.buyer_tier === 'pro_volume' || userProfile?.role === 'admin';
    if (!isProOrAdmin) {
      if (window.confirm("The Side-by-Side Supplier Arbitrage Matrix is a Pro Buyer feature ($99/mo Standard or $199/mo High Volume). Would you like to view upgrade options?")) {
        navigate('/billing?modules=pro_buyer_standard');
      }
      return;
    }
    setShowMatrix(true);
  };

  if (loading && suppliers.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading...</h3>
        </div>
      </div>
    );
  }

  const activeSourcingTerm = activeSearchTerm || t('common.this_category');

  return (
    <div className="min-h-screen bg-gray-50 font-sans pb-24">
      
      {/* HERO HEADER */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex-1 min-w-0">
              <h1 className="text-3xl font-bold text-gray-900 sm:text-4xl mb-3 tracking-tight">
                {t('directory.title')}
              </h1>
              <p className="text-lg text-gray-600 max-w-2xl">
                {t('directory.subtitle')}
              </p>
              
              {/* ⚡ REPLACED RAW COUNT WITH CURATED BADGE */}
              <div className="flex items-center mt-5 gap-2">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-bold uppercase tracking-wider text-slate-700 bg-slate-100 px-3 py-1 rounded-full border border-slate-200">
                  {t('directory.pre_vetted_badge', 'Pre-Vetted Core & Shell Network')}
                </span>
              </div>
            </div>

            {/* YOUR ORIGINAL BUTTONS */}
            <div className="mt-2 lg:mt-0">
              {user ? (
                <>
                  {isSupplier ? (
                    <Link
                      to="/products/new"
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-bold rounded-lg shadow-md text-white bg-blue-600 hover:bg-blue-700 transition-all hover:shadow-lg"
                    >
                      <span className="mr-2 text-xl leading-none">+</span>
                      {t('directory.add_product')}
                    </Link>
                  ) : (
                    <Link
                      to="/supplier-onboarding"
                      className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-bold rounded-lg shadow-md text-white bg-green-600 hover:bg-green-700 transition-all hover:shadow-lg"
                    >
                      {t('directory.list_business')}
                    </Link>
                  )}
                </>
              ) : (
                <Link
                  to="/login"
                  className="inline-flex items-center justify-center px-6 py-3 border border-blue-600 text-base font-medium rounded-lg text-blue-600 bg-white hover:bg-blue-50"
                >
                  {t('directory.sign_in_list')}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {error && (
          <div className="mb-8 bg-red-50 border-l-4 border-red-500 p-4 rounded-r">
            <p className="text-sm text-red-700 font-bold">{error}</p>
          </div>
        )}

        {!showMatrix && (
          <SearchFilters 
            onSearch={handleSearchSubmit} 
            isLoading={searchLoading} 
          />
        )}

        {showMatrix ? (
          <div className="animate-fade-in">
            <button 
              onClick={() => setShowMatrix(false)} 
              className="mb-6 flex items-center text-sm font-bold text-blue-600 hover:text-blue-800 transition-colors bg-white px-4 py-2 rounded-lg border border-blue-200 shadow-sm inline-flex"
            >
              <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18"></path></svg>
              {t('directory.back_to_directory')}
            </button>
            
            <ArbitrageMatrix 
              selectedSuppliers={selectedSuppliers} 
              buyerProvince={activeProvince || 'ON'}
              targetHsCode={activeSearchTerm} 
            />
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">{t('directory.available_suppliers')}</h2>
              <span className="text-sm font-medium text-gray-500 bg-gray-100 px-3 py-1 rounded-full">{t('directory.results', { count: suppliers.length })}</span>
            </div>

            {suppliers.length > 0 ? (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {suppliers.map(supplier => {
                    const isSelected = selectedSuppliers.some(s => s.id === supplier.id);
                    return (
                      <div 
                        key={supplier.id} 
                        className={`relative rounded-xl transition-all duration-200 ${isSelected ? 'ring-4 ring-blue-500 transform scale-[1.02]' : ''}`}
                      >
                        <div className="absolute top-4 right-4 z-10 bg-white rounded-md shadow-sm border border-gray-200 p-1 flex items-center gap-2 cursor-pointer" onClick={() => handleToggleSelect(supplier)}>
                          <input 
                            type="checkbox" 
                            checked={isSelected}
                            readOnly
                            className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500 cursor-pointer pointer-events-none"
                          />
                          <span className="text-xs font-bold text-gray-700 select-none hidden sm:inline-block pr-1">{t('directory.compare')}</span>
                        </div>

                        <SupplierCard
                          supplier={supplier}
                          showEditButton={user && supplier.user_id === user.id}
                          onContact={handleDirectRfqRoute} 
                        />
                      </div>
                    );
                  })}
                </div>

                {suppliers.length < 3 && !activeShowMyListings && (
                  <div className="mt-8 bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between shadow-sm animate-fade-in">
                    <div className="mb-6 md:mb-0 md:pr-8 text-center md:text-left">
                      <h3 className="text-lg font-bold text-gray-900 flex items-center justify-center md:justify-start gap-2">
                        <span className="text-2xl">🕵️</span> {t('directory.want_more')}
                      </h3>
                      <p className="text-sm text-gray-600 mt-2 max-w-2xl">
                        {t('directory.want_more_desc', { term: activeSourcingTerm })}
                      </p>
                    </div>
                    {/* ⚡ FIX 1: Updated fallback text to 'Concierge Sourcing Request' */}
                    <button
                      onClick={() => navigate('/rfq/new', { state: { autoFillCode: activeSearchTerm, autoFillProvince: activeProvince } })}
                      className="bg-gray-900 text-white px-8 py-3.5 rounded-lg font-bold hover:bg-gray-800 transition-colors shadow-md whitespace-nowrap w-full md:w-auto"
                    >
                      {t('directory.post_custom_rfq', 'Concierge Sourcing Request')}
                    </button>
                  </div>
                )}
              </>
            ) : (
              !error && !searchLoading && (
                <div className="bg-white p-12 rounded-xl border border-gray-200 text-center shadow-sm animate-fade-in">
                  {activeShowMyListings ? (
                    <>
                      <div className="mx-auto w-16 h-16 bg-blue-50 rounded-full flex items-center justify-center mb-4">
                        <span className="text-2xl text-blue-500">📦</span>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 mb-2">{t('directory.no_listings_title')}</h3>
                      <p className="text-gray-500 max-w-sm mx-auto mb-6 text-sm">
                        {t('directory.no_listings_desc')}
                      </p>
                      <button 
                        onClick={() => navigate('/products/new')}
                        className="bg-blue-600 text-white px-6 py-2.5 rounded-lg font-bold hover:bg-blue-700 transition-colors shadow-sm"
                      >
                        {t('directory.add_product')}
                      </button>
                    </>
                  ) : (
                    <>
                      <div className="mx-auto w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mb-4">
                        <span className="text-2xl text-amber-500">🏗️</span>
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-2">{t('directory.sourcing_required_title')}</h3>
                      <p className="text-gray-600 max-w-md mx-auto mb-6 text-sm">
                        {t('directory.sourcing_required_desc', { term: activeSourcingTerm })}
                      </p>
                      {/* ⚡ FIX 2: Updated fallback text to 'Concierge Sourcing Request' */}
                      <button 
                        onClick={() => navigate('/rfq/new', { state: { autoFillCode: activeSearchTerm, autoFillProvince: activeProvince } })}
                        className="bg-gray-900 text-white px-6 py-3 rounded-lg font-bold hover:bg-gray-800 transition-colors shadow-md animate-pulse"
                      >
                        {t('directory.concierge_btn', 'Concierge Sourcing Request')}
                      </button>
                    </>
                  )}
                </div>
              )
            )}
          </>
        )}
      </div>

      {/* STICKY FLOATING ACTION BAR FOR MATRIX */}
      {!showMatrix && selectedSuppliers.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-[0_-10px_15px_-3px_rgba(0,0,0,0.1)] p-4 z-50 animate-slide-up">
          <div className="max-w-7xl mx-auto w-full flex flex-col sm:flex-row justify-between items-center gap-4">
            <div className="flex items-center">
              <div className="flex items-center justify-center w-8 h-8 rounded-full bg-blue-100 text-blue-700 font-bold mr-3">
                {selectedSuppliers.length}
              </div>
              <span className="text-gray-800 font-medium">
                {t('directory.selected_for_arbitrage')} <span className="text-gray-400 text-sm">{t('directory.max_limit')}</span>
              </span>
            </div>
            <div className="flex gap-3 w-full sm:w-auto">
              <button 
                onClick={() => setSelectedSuppliers([])} 
                className="flex-1 sm:flex-none text-gray-600 hover:text-gray-900 font-bold bg-gray-100 hover:bg-gray-200 px-6 py-2.5 rounded-lg transition-colors"
              >
                {t('directory.clear')}
              </button>
              <button 
                onClick={handleLaunchMatrix} 
                className="flex-1 sm:flex-none bg-blue-900 hover:bg-blue-800 text-white px-8 py-2.5 rounded-lg font-bold shadow-md transition-colors whitespace-nowrap"
              >
                {t('directory.run_ai')}
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default SupplierDirectory;